import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { cancelSubscription } from "../services/subscriptionsService";
import { fetchTrialAccess } from "../services/trialService";
import { TRIAL_ACCESS_QUERY_KEY } from "./useTrialAccess";
import type {
  AccessSource,
  Subscription,
  Trial,
  TrialAccess,
} from "../types/subscriptions.types";

export type CurrentSubscriptionStatus = "loading" | "ready" | "failed";

export interface CurrentSubscriptionState {
  /** What the workspace is on, or `null` for one that has never subscribed. */
  subscription: Subscription | null;
  /**
   * The free trial, where there has been one — running or finished.
   *
   * Read from the same answer, and carried for the screens that draw it: the
   * countdown, the meter, the "trial ended" wording. It is *not* what decides
   * whether the product is open — that is `accessSource` below, whichever of
   * the two is holding the door.
   */
  trial: Trial | null;
  /**
   * What is holding the door open — `SUBSCRIPTION`, `FREE_TRIAL` or `NONE` —
   * and the only thing any access decision reads. `null` until the read has
   * answered, and for an answer that did not say. See `hasAccess`.
   */
  accessSource: AccessSource | null;
  status: CurrentSubscriptionStatus;
  error?: string;
  reload: () => void;
  /** Stops it renewing, at the end of the cycle already paid for. */
  cancel: () => Promise<Subscription>;
}

/**
 * What the workspace is subscribed to.
 *
 * `null` and `ready` for a workspace that has never subscribed — which is not
 * a failure and must not be drawn as one: it is the ordinary state of every
 * workspace before its first purchase, and the Billing screen simply shows the
 * plans.
 *
 * It asks `GET /billing/subscriptions/` outright. It used to be held behind
 * `SUBSCRIPTIONS_LIVE`, because the endpoint it read did not exist; this one
 * does, and so does the cancel below it — both are the billing API's now.
 *
 * The refund is not here. It has an endpoint of its own that answers far more
 * than a subscription record could — whether it can be asked for, until when,
 * against which payment, and with which reasons — so it is `useRefundOffer`'s,
 * and this hook has nothing to say about one.
 *
 * Reads through the same TanStack Query key as `useTrialAccess` — both call
 * `GET /api/v1/billing/subscription/`, and sharing the key means one fetch
 * serves both instead of each issuing its own. Cached across mounts like every
 * other resource here: a mandate confirmed on another device, or hours after a
 * checkout was closed, still catches up on the next visit rather than sitting
 * behind a load every time this screen opens.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useCurrentSubscription(): CurrentSubscriptionState {
  const getToken = useTokenGetter();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: TRIAL_ACCESS_QUERY_KEY,
    queryFn: ({ signal }) => fetchTrialAccess(getToken, signal),
  });

  useEffect(() => {
    if (query.isError) {
      console.error("subscription failed to load:", query.error);
    }
  }, [query.isError, query.error]);

  const subscription = query.data?.subscription ?? null;

  /**
   * Cancels at the end of the paid cycle — see `CancelSubscriptionModal`, which
   * is where that promise is made to the user.
   *
   * It answers with the record as it now stands and puts it straight into the
   * cache, so the card redraws from what the backend said rather than from
   * what this hook assumed would happen. What it says is quieter than it looks:
   * the record comes back `ACTIVE` with its period end unmoved, and
   * `cancelledOn` newly set. That field is the news.
   *
   * The subscription is not sent — the endpoint takes no id, the token says
   * whose workspace it is — but it is still checked for, because a Cancel
   * pressed with nothing on the record is a bug at the call site rather than a
   * request worth making.
   */
  const cancel = useCallback(async () => {
    if (!subscription) {
      throw new Error("No subscription to cancel.");
    }

    const cancelled = await cancelSubscription(getToken);
    queryClient.setQueryData<TrialAccess>(
      TRIAL_ACCESS_QUERY_KEY,
      (current: TrialAccess | undefined) =>
        current ? { ...current, subscription: cancelled } : current
    );
    return cancelled;
  }, [getToken, subscription, queryClient]);

  return {
    subscription,
    trial: query.data?.trial ?? null,
    accessSource: query.data?.accessSource ?? null,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    error:
      query.isError && query.error instanceof ApiError
        ? query.error.message
        : query.isError
        ? "We could not reach the server. Check your connection and try again."
        : undefined,
    reload: () => {
      void query.refetch();
    },
    cancel,
  };
}
